import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, FileText, RotateCcw, Wallet, X } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";

const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";
const STATUS_LABEL = { open: "Belum dibayar", paid: "Sudah dibayar", cancelled: "Dibatalkan" };
const CYCLE_LABEL = { monthly: "Bulanan", biweekly: "Dua mingguan", weekly: "Mingguan",
  manual: "Manual" };

const Badge = ({ status, overdue }) => (
  <span className={`text-[11px] px-2 py-1 rounded-full border ${
    status === "paid" ? "border-emerald-600/40 text-emerald-700"
      : status === "cancelled" ? "border-black/15 text-ink-soft"
        : overdue ? "border-lm-red text-lm-red" : "border-amber-600/40 text-amber-700"}`}>
    {STATUS_LABEL[status]}{status === "open" && overdue ? " · lewat jatuh tempo" : ""}
  </span>
);

const PayForm = ({ payout, onDone }) => {
  const [f, setF] = useState({ reference_no: "", paid_date: new Date().toISOString().slice(0, 10),
    note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.reference_no.trim()) return toast.error("Nomor referensi transfer wajib diisi");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reference_no", f.reference_no);
      fd.append("paid_date", f.paid_date);
      fd.append("note", f.note);
      if (file) fd.append("file", file);
      await api.post(`/payouts/${payout.id}/pay`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pembayaran komisi dicatat");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-black/10 pt-3 grid sm:grid-cols-2 gap-3"
      data-testid={`payout-pay-form-${payout.id}`}>
      <label className="block"><span className="text-[10px] uppercase tracking-widest text-ink-soft">Nomor referensi transfer</span>
        <Input className="mt-1 h-10" data-testid={`payout-ref-${payout.id}`} value={f.reference_no}
          onChange={(e) => setF({ ...f, reference_no: e.target.value })} /></label>
      <label className="block"><span className="text-[10px] uppercase tracking-widest text-ink-soft">Tanggal transfer</span>
        <Input type="date" className="mt-1 h-10" data-testid={`payout-date-${payout.id}`}
          value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} /></label>
      <label className="block sm:col-span-2"><span className="text-[10px] uppercase tracking-widest text-ink-soft">Bukti transfer (JPG/PNG/PDF, maks 10MB)</span>
        <input type="file" accept="image/*,application/pdf" data-testid={`payout-proof-${payout.id}`}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mt-1 block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-ink file:px-3 file:py-2 file:text-cream" /></label>
      <label className="block sm:col-span-2"><span className="text-[10px] uppercase tracking-widest text-ink-soft">Catatan</span>
        <Input className="mt-1 h-10" data-testid={`payout-note-${payout.id}`} value={f.note}
          onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
      <button data-testid={`payout-confirm-${payout.id}`} onClick={submit} disabled={busy}
        className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
        {busy ? "Menyimpan…" : "Simpan pembayaran"}
      </button>
    </div>
  );
};

const Detail = ({ payout, canPay, canReopen, reload }) => {
  const [d, setD] = useState(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    api.get(`/payouts/${payout.id}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [payout.id]);

  const cancel = async () => {
    const reason = window.prompt("Alasan membatalkan batch ini?");
    if (!reason) return;
    try {
      await api.post(`/payouts/${payout.id}/cancel`, { reason });
      toast.success("Batch dibatalkan, komisi dilepas kembali");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const reopen = async () => {
    const reason = window.prompt("Alasan membuka kembali batch yang sudah dibayar?");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/payouts/${payout.id}/reopen`, { reason });
      toast.success("Batch dibuka kembali");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-4 bg-cream-dark/40" data-testid={`payout-detail-${payout.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        {payout.status === "open" && canPay && (
          <>
            <button data-testid={`payout-pay-${payout.id}`} onClick={() => setPaying(!paying)}
              className="lm-btn-primary rounded-md px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} /> Tandai dibayar
            </button>
            <button data-testid={`payout-cancel-${payout.id}`} onClick={cancel}
              className="rounded-md border border-black/15 px-4 py-2 text-xs inline-flex items-center gap-1.5">
              <X size={13} /> Batalkan batch
            </button>
          </>
        )}
        {payout.status === "paid" && (
          <>
            <span className="text-xs text-ink-soft">
              Ref <b className="text-ink">{payout.reference_no}</b> · {payout.paid_date} · oleh {payout.paid_by}
            </span>
            {payout.proof ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${payout.proof.url}`} target="_blank"
                rel="noreferrer" data-testid={`payout-proof-link-${payout.id}`}
                className="text-xs text-lm-red hover:underline inline-flex items-center gap-1.5">
                <FileText size={13} /> Lihat bukti transfer ({payout.proof.filename})
              </a>
            ) : <span className="text-xs text-amber-700">Tanpa bukti transfer</span>}
            {canReopen && (
              <button data-testid={`payout-reopen-${payout.id}`} onClick={reopen}
                className="rounded-md border border-black/15 px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
                <RotateCcw size={12} /> Buka kembali
              </button>
            )}
          </>
        )}
      </div>
      {paying && payout.status === "open" && (
        <PayForm payout={payout} onDone={() => { setPaying(false); reload(); }} />
      )}

      <div className="mt-4 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-cream-dark text-left text-[10px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-2.5">Booking</th><th className="p-2.5">Kategori</th>
              <th className="p-2.5">Dasar</th><th className="p-2.5">Aturan</th>
              <th className="p-2.5">Komisi</th><th className="p-2.5">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {(d?.rows || []).map((r) => (
              <tr key={r.id} data-testid={`payout-row-${r.booking_code}`}>
                <td className="p-2.5 font-medium">{r.booking_code}</td>
                <td className="p-2.5">{r.category_label}</td>
                <td className="p-2.5">{rp(r.base_amount)}</td>
                <td className="p-2.5">{r.breakdown}</td>
                <td className="p-2.5">{rp(r.amount)}</td>
                <td className="p-2.5">{r.status}</td>
              </tr>
            ))}
            {d && (d.rows || []).length === 0 && (
              <tr><td colSpan={6} className="p-3 text-ink-soft">Tidak ada baris komisi.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">Riwayat</div>
        <div className="mt-1 space-y-1">
          {(payout.history || []).map((h) => (
            <div key={`${h.id || h.at}-${h.action}-${h.by}`} className="text-[11px] text-ink-soft"
              data-testid={`payout-history-${payout.id}-${h.id || h.at}`}>
              {String(h.at).slice(0, 16).replace("T", " ")} · {h.action} · {h.by}
              {h.note ? ` · ${h.note}` : ""}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Payouts() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState({ hub_id: "", status: "" });
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/payouts", { params: { hub_id: q.hub_id || undefined, status: q.status || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat pembayaran komisi"));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const generate = async (force) => {
    setBusy(true);
    try {
      const r = await api.post("/payouts/generate", {
        hub_id: q.hub_id || undefined, force: !!force });
      const made = r.data.created.length;
      toast[made ? "success" : "info"](made
        ? `${made} batch dibuat`
        : `Tidak ada batch baru: ${(r.data.skipped[0]?.reason) || "tidak ada komisi baru"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat batch");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="payouts-page">
      <h1 className="font-display text-3xl tracking-tight">Pembayaran Komisi</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Komisi yang sudah jadi hak dikumpulkan per Hub per periode. Tandai sudah dibayar, unggah
        bukti transfer, dan semuanya tersimpan di riwayat — termasuk daftar booking di dalam batch.
      </p>

      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <select className={sel} data-testid="payout-filter-hub" value={q.hub_id}
          onChange={(e) => setQ({ ...q, hub_id: e.target.value })}>
          <option value="">Semua Hub</option>
          {(data?.hubs || []).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <select className={sel} data-testid="payout-filter-status" value={q.status}
          onChange={(e) => setQ({ ...q, status: e.target.value })}>
          <option value="">Semua status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {data?.can_pay && (
          <>
            <button data-testid="payout-generate" onClick={() => generate(false)} disabled={busy}
              className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
              {busy ? "Memproses…" : "Buat batch periode jatuh tempo"}
            </button>
            <button data-testid="payout-generate-force" onClick={() => generate(true)} disabled={busy}
              className="rounded-md border border-black/15 px-4 py-2.5 text-sm">
              Buat sekarang (tanpa tunggu jatuh tempo)
            </button>
          </>
        )}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {[["Belum dibayar", data?.totals?.open, "payout-total-open"],
          ["Sudah dibayar", data?.totals?.paid, "payout-total-paid"],
          ["Dibatalkan", data?.totals?.cancelled, "payout-total-cancelled"]].map(([l, v, tid]) => (
          <div key={tid} className="bg-white border border-black/10 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-ink-soft">{l}</div>
            <div className="mt-1 font-display text-2xl" data-testid={tid}>{rp(v || 0)}</div>
          </div>
        ))}
      </div>

      {(data?.hubs || []).some((h) => h.schedule) && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="payout-schedules">
          {(data?.hubs || []).map((h) => (
            <span key={h.id} data-testid={`payout-schedule-${h.code}`}
              className="text-[11px] px-3 py-1.5 rounded-full border border-black/15 inline-flex items-center gap-1.5">
              <CalendarClock size={12} /> {h.name}: {CYCLE_LABEL[h.schedule.cycle]}
              {h.schedule.cycle === "monthly" ? ` tgl ${h.schedule.pay_day}` : ""}
              {h.schedule.enabled ? "" : " (mati)"}
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-3">
        {(data?.payouts || []).map((p) => (
          <div key={p.id} className="bg-white border border-black/10 rounded-lg overflow-hidden"
            data-testid={`payout-card-${p.id}`}>
            <button onClick={() => setOpen(open === p.id ? "" : p.id)}
              data-testid={`payout-toggle-${p.id}`}
              className="w-full text-left p-4 flex flex-wrap items-center gap-3 hover:bg-cream-dark/40">
              <Wallet size={15} className="text-lm-red" />
              <span className="font-medium text-sm">{p.hub_name}</span>
              <span className="text-xs text-ink-soft">
                {p.period_start} s/d {p.period_end} · {p.bookings_count} booking · jatuh tempo {p.due_date}
              </span>
              <span className="ml-auto font-semibold">{rp(p.total_amount)}</span>
              <Badge status={p.status} overdue={p.overdue} />
            </button>
            {open === p.id && (
              <Detail payout={p} canPay={data.can_pay} canReopen={data.can_reopen} reload={load} />
            )}
          </div>
        ))}
        {data && (data.payouts || []).length === 0 && (
          <div className="bg-white border border-black/10 rounded-lg p-6 text-sm text-ink-soft"
            data-testid="payouts-empty">
            Belum ada batch pembayaran. Batch dibuat otomatis setiap 06:00 WIB saat periode Hub
            jatuh tempo, atau tekan tombol di atas untuk membuatnya sekarang.
          </div>
        )}
      </div>
    </div>
  );
}
